import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Search, Trash2, Loader2, Upload, Star, MapPin,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAddressBookPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [busy, setBusy] = useState(false);

  // Import panel state
  const [imp, setImp] = useState({
    target_user_email: '',
    type: 'ship_to',
    replace: false,
    text: '',   // JSON array OR CSV
  });
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (typeFilter !== 'all') params.set('type', typeFilter);
      const r = await fetch(`${API}/admin/address-book/all?${params.toString()}`, { headers: auth });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to load');
      const d = await r.json();
      setRows(d.addresses || []);
    } catch (e) {
      toast.error(e.message);
      setRows([]);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);

  const remove = async (id, label) => {
    if (!window.confirm(`Delete address for ${label}?`)) return;
    try {
      const r = await fetch(`${API}/admin/address-book/${id}`, { method: 'DELETE', headers: auth });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const parseImportRows = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    // Try JSON first
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [parsed];
    }
    // Fallback: simple CSV with header row
    const lines = trimmed.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim());
      const row = {};
      headers.forEach((h, i) => { row[h] = cols[i] || ''; });
      return row;
    });
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const addresses = parseImportRows(imp.text);
      if (!addresses.length) throw new Error('Paste JSON array or CSV with header row.');
      const r = await fetch(`${API}/admin/address-book/import`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          target_user_email: imp.target_user_email.trim(),
          type: imp.type,
          replace: imp.replace,
          addresses,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Import failed');
      toast.success(`Imported ${d.imported} address(es) to ${d.target_email}${d.skipped ? ` · skipped ${d.skipped}` : ''}`);
      setImp({ ...imp, text: '' });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setImporting(false); }
  };

  const filtered = useMemo(() => {
    const list = rows || [];
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter(r =>
      (r.name || '').toLowerCase().includes(s) ||
      (r.company || '').toLowerCase().includes(s) ||
      (r.street1 || '').toLowerCase().includes(s) ||
      (r.city || '').toLowerCase().includes(s) ||
      (r.state || '').toLowerCase().includes(s) ||
      (r.zip_code || '').toLowerCase().includes(s) ||
      (r.owner_email || '').toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-address-book-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-500" /> Admin — All Shipper Address Books
        </h1>
        <p className="text-slate-500 mt-1">Every address across every shipper. Import from other systems below.</p>
      </div>

      {/* Import panel */}
      <Card data-testid="import-panel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-emerald-500" /> Import from another system</CardTitle>
          <CardDescription>Paste a JSON array (or CSV with header row) exported from SelleCheck, SellePack, or any other system. Attach to any merchant by email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Attach to merchant (email) *</Label>
              <Input
                value={imp.target_user_email}
                onChange={(e) => setImp({ ...imp, target_user_email: e.target.value })}
                placeholder="merchant@example.com"
                data-testid="import-target-email"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={imp.type} onValueChange={(v) => setImp({ ...imp, type: v })}>
                <SelectTrigger data-testid="import-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ship_to">Recipient (ship_to)</SelectItem>
                  <SelectItem value="ship_from">Sender (ship_from)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm mt-6" data-testid="import-replace">
              <input
                type="checkbox"
                checked={imp.replace}
                onChange={(e) => setImp({ ...imp, replace: e.target.checked })}
              />
              Replace existing of this type
            </label>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Rows — JSON array or CSV *</Label>
            <Textarea
              rows={6}
              value={imp.text}
              onChange={(e) => setImp({ ...imp, text: e.target.value })}
              placeholder={`Example JSON:\n[{"name":"John","street1":"123 Main St","city":"San Diego","state":"CA","zip_code":"92105","country":"US","phone":"","email":""}]\n\nExample CSV (header row required):\nname,company,street1,city,state,zip_code,country,phone,email`}
              className="font-mono text-xs"
              data-testid="import-text"
            />
          </div>
          <Button onClick={runImport} disabled={importing || !imp.target_user_email || !imp.text} data-testid="import-run">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Import addresses
          </Button>
        </CardContent>
      </Card>

      {/* Search + filters */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px] space-y-1">
            <Label className="text-xs">Search across all shippers</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                className="pl-9"
                placeholder="Name, company, street, ZIP, or owner email…"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="space-y-1 w-44">
            <Label className="text-xs">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ship_from">Sender</SelectItem>
                <SelectItem value="ship_to">Recipient</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={busy} variant="outline" data-testid="search-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Search
          </Button>
        </CardContent>
      </Card>

      {/* Results table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Addresses ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Owner</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Type</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Name / Company</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Address</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">City · State · ZIP</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase text-right">Used</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows === null ? (
                  <tr><td colSpan={7} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-400">No addresses found.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`row-${r.id.slice(0,8)}`}>
                    <td className="py-2 px-2">
                      <div className="text-xs font-medium">{r.owner_email || '—'}</div>
                      <div className="text-[11px] text-slate-500">{r.owner_company || r.owner_role || ''}</div>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${r.type === 'ship_from' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {r.type === 'ship_from' ? 'Sender' : 'Recipient'}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <div className="font-medium flex items-center gap-1">
                        {r.is_default && <Star className="w-3 h-3 fill-amber-500 text-amber-500" />}
                        {r.name || '—'}
                      </div>
                      {r.company && <div className="text-xs text-slate-500">{r.company}</div>}
                    </td>
                    <td className="py-2 px-2 text-slate-600 dark:text-slate-300">
                      <div className="flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-1 text-slate-400 shrink-0" />
                        <span>{r.street1}{r.street2 ? `, ${r.street2}` : ''}</span>
                      </div>
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {r.city}, {r.state} {r.zip_code}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.use_count || 0}</td>
                    <td className="py-2 px-2 text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => remove(r.id, `${r.name || r.company || ''} @ ${r.owner_email || ''}`)}
                        data-testid={`delete-${r.id.slice(0,8)}`}
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
